import { Loading } from "../../../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import {
  ASSET_KIND_LABELS,
  CONDITION_SCORE_LABELS,
  formatEuro,
  formatIntervalOf,
  LIBRARY_UNIT_LABELS,
  type AssetDetail,
  type ConditionScore,
  type FloorDto,
  type PlanAssetDto,
  type RoomDto,
} from "@klantpunt/shared";
import { api } from "../../../lib/api";
import { hasModule, useMe } from "../../../lib/auth";
import { thumbUrl } from "../../../lib/photos";
import { assetKey } from "../../assets/AssetPanel";
import { useBuilding } from "../../building/useBuilding";
import { loadPlanRaster } from "../../report/browser";
import { snippetBox } from "../../report/buildReport";
import { fmtDay } from "./periods";
import { Drawer } from "./ui";

/**
 * ⓘ bij een asset in de MJOP-planning (docs/ASSETS-MJOP.md, "Planning") en de keuringenlijst: foto's,
 * de plek op de tekening en de gegevens van de asset, zonder de lijst te verlaten. Vervanging en
 * uitvoeringen alleen met de MJOP-regels (`a.rows`).
 */
export function AssetInfo({
  buildingId,
  a,
  groupLabel,
  onClose,
}: {
  buildingId: string;
  a: Pick<PlanAssetDto, "id" | "code" | "name"> & Partial<Pick<PlanAssetDto, "unit" | "rows">>;
  /** NL-SfB-groep, bv. "66.1 Liften". */
  groupLabel: string;
  onClose: () => void;
}) {
  const { data: me } = useMe();
  const b = useBuilding(buildingId).data;
  const q = useQuery({ queryKey: assetKey(a.id), queryFn: () => api<AssetDetail>(`/assets/${a.id}`) });
  const d = q.data;
  const photos = d?.files.filter((f) => f.kind === "photo") ?? [];
  const floor = d?.floorId ? b?.floors.find((f) => f.id === d.floorId) : undefined;
  const room = d?.roomId ? b?.rooms.find((r) => r.id === d.roomId) : undefined;
  const repl = a.rows?.find((r) => r.type === "vervanging");
  const executions = a.rows?.filter((r) => r.source === "obligation").length;
  const unit = d?.unit ? LIBRARY_UNIT_LABELS[d.unit] : (a.unit ?? "");
  const link = `/gebouwen/${buildingId}?asset=${a.id}`;

  const facts: [string, ReactNode][] = d
    ? [
        ["Code", d.code],
        ["NL-SfB", groupLabel],
        ["Soort", ASSET_KIND_LABELS[d.assetKind]],
        ["Merk en type", [d.brand, d.model].filter(Boolean).join(" ") || null],
        ["Serienummer", d.serialNumber],
        ["Capaciteit", d.capacity],
        ["Hoeveelheid", d.quantity != null ? `${d.quantity.toLocaleString("nl-NL")} ${unit}`.trim() : null],
        ["Bouwjaar", d.yearBuilt],
        ["Eind garantie", d.warrantyUntil ? fmtDay(d.warrantyUntil) : null],
        [
          "Conditie",
          hasModule(me, "condition") ? (
            d.conditionScore != null ? (
              <span className="pl-cond">
                <span className={`condscore cond-${d.conditionScore}`} aria-hidden="true">
                  {d.conditionScore}
                </span>
                {CONDITION_SCORE_LABELS[d.conditionScore as ConditionScore]}
                {d.conditionAssessedOn && ` · NEN 2767, ${fmtDay(d.conditionAssessedOn)}`}
              </span>
            ) : (
              <span className="muted">Nog niet gemeten</span>
            )
          ) : null,
        ],
        [
          "Vervanging",
          !a.rows ? null : repl ? (
            `${repl.intervalMonths ? `elke ${formatIntervalOf(repl.intervalMonths, null)}, ` : ""}eerstvolgende ${
              repl.start?.slice(0, 4) ?? "—"
            }${repl.costCents != null ? ` · ${formatEuro(repl.costCents)}` : ""}`
          ) : (
            <span className="muted">Geen levensduur ingevuld</span>
          ),
        ],
        ["Uitvoeringen", a.rows ? executions || "geen" : null],
        ["QR-label", d.qrCode ? "Gekoppeld" : <span className="muted">Geen</span>],
      ]
    : [];

  return (
    <Drawer
      eyebrow={`Asset · ${groupLabel}`}
      title={
        <>
          <span className="pl-acode">{a.code}</span>
          {a.name}
        </>
      }
      onClose={onClose}
      footer={
        <>
          <Link className="btn" to={link}>
            Asset openen
          </Link>
          <button type="button" className="btn primary" onClick={onClose}>
            Sluiten
          </button>
        </>
      }
    >
      {!d ? (
        q.isError ? (
          <p className="muted">{"Asset niet gevonden."}</p>
        ) : (
          <Loading />
        )
      ) : (
        <>
          <section className="pl-sect" aria-label="Foto's">
            <h3>Foto's</h3>
            {photos.length ? (
              <div className="pl-photos">
                {photos.slice(0, 6).map((p) => (
                  <img key={p.fileId} src={thumbUrl(p.fileId)} alt={p.name || "Foto"} loading="lazy" />
                ))}
              </div>
            ) : (
              <p className="muted small">
                Nog geen foto's. <Link to={link}>Foto toevoegen bij de asset</Link>
              </p>
            )}
            {photos.length > 6 && <p className="muted small">en nog {photos.length - 6}</p>}
          </section>
          <section className="pl-sect" aria-label="Op de plattegrond">
            <h3>Op de plattegrond</h3>
            {floor && d.x != null && d.y != null ? (
              <figure className="pl-plan">
                <PlanSnippet floor={floor} x={d.x} y={d.y} room={room} />
                <figcaption>
                  <span>{[floor.name, room?.name].filter(Boolean).join(" · ")}</span>
                  <Link className="btn small" to={link}>
                    Open tekening
                  </Link>
                </figcaption>
              </figure>
            ) : (
              <p className="muted small">
                {floor
                  ? `${floor.name}${room ? ` · ${room.name}` : ""}, niet op de tekening geplaatst.`
                  : "Geen vaste plek."}
              </p>
            )}
          </section>
          <section className="pl-sect" aria-label="Details">
            <h3>Details</h3>
            <dl className="pl-facts">
              {facts
                .filter(([, v]) => v != null && v !== "")
                .map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
            </dl>
            {d.notes && <p className="small pl-notes">{d.notes}</p>}
          </section>
        </>
      )}
    </Drawer>
  );
}

/** Uitsnede van de tekening rond de asset (zoals in het rapport), met ruimte en pin. */
function PlanSnippet({ floor, x, y, room }: { floor: FloorDto; x: number; y: number; room?: RoomDto | undefined }) {
  const q = useQuery({
    queryKey: ["plan-raster", floor.id, floor.plan?.id, floor.planPage],
    queryFn: () => loadPlanRaster(floor),
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
  const r = q.data;
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!r) return;
    const url = URL.createObjectURL(new Blob([r.jpeg as BlobPart], { type: "image/jpeg" }));
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [r]);
  // Breder dan hoog (10:7), zodat de gegevens eronder in beeld blijven.
  const box = useMemo(() => {
    if (!r) return null;
    const { sx, side } = snippetBox(r.width, r.height, x, y);
    const h = Math.min(side * 0.7, r.height);
    return { sx, sy: Math.max(0, Math.min(r.height - h, y * r.height - h / 2)), side, h };
  }, [r, x, y]);
  if (!floor.plan) return <p className="muted small">Geen tekening bij {floor.name}.</p>;
  if (q.isError || (q.isSuccess && !r)) return <p className="muted small">Tekening kon niet worden geladen.</p>;
  if (!r || !box || !src) return <div className="pl-planload" aria-busy="true" />;
  const { sx, sy, side, h } = box;
  const W = r.width;
  const H = r.height;
  const pts = room?.shape?.points.map((p) => `${p[0] * W},${p[1] * H}`).join(" ");
  return (
    <svg viewBox={`${sx} ${sy} ${side} ${h}`} role="img" aria-label={`Uitsnede van ${floor.name} met de asset`}>
      <image href={src} x={0} y={0} width={W} height={H} />
      {pts && <polygon points={pts} className="pl-room" strokeWidth={side / 160} />}
      <circle cx={x * W} cy={y * H} r={side / 18} className="pl-halo" />
      <circle cx={x * W} cy={y * H} r={side / 42} className="pl-pin" strokeWidth={side / 120} />
    </svg>
  );
}
